/**
 * What to call a person.
 *
 * A display name is optional — it is asked for once, on the way in, and left
 * blank by anyone in a hurry. So there has to be a name to fall back on, and it
 * has to be the SAME name everywhere: the header, the people list, and the
 * membership document a Cloud Function writes days apart from each other. A
 * fallback spelled twice is two names for one person, and the one in the member
 * document is the one nobody can correct from inside the app.
 *
 * The fallback is the local part of the email address, not the whole address.
 * "anna" is what a person is called; "anna@example.com" is how you reach her,
 * and it is also the one string in this app that should not sit in a header on
 * a tablet in a room with other people in it.
 *
 * This file has no zod import on purpose. The Cloud Functions import it by its
 * own subpath (`@puppy-doctor/platform/people`) precisely so that a helper this
 * small does not drag the schemas — and 600 KB of zod's translated error
 * messages — into a function bundle. See functions/build.ts.
 */

/** Anything with a name and an address on it: a profile, a member, a token. */
export type Named = {
  displayName?: string | null | undefined;
  email?: string | null | undefined;
};

/**
 * The name to show, given whatever is known about someone. Never throws and
 * never returns `undefined`; it is fed straight from Firestore documents and
 * auth tokens, both of which may have been written by an older build.
 *
 * Empty only when there is nothing at all to go on — a caller with a uid in
 * hand may want to fall back to that, which is why this does not invent one.
 */
export const displayNameOf = (person: Named): string => {
  const chosen = person.displayName?.trim() ?? '';
  if (chosen !== '') return chosen;
  return emailNameOf(person.email);
};

/**
 * The local part, as a name. Split rather than a regex because the only thing
 * being asked here is "what is before the @", and an address that somehow has
 * no @ in it is better shown whole than shown empty.
 */
export const emailNameOf = (email: string | null | undefined): string => {
  const address = email?.trim() ?? '';
  if (address === '') return '';
  return address.split('@')[0] ?? address;
};
